/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { Fragment, useState } from 'react'
import { useIntl } from 'react-intl'
import { jsx, css } from '@emotion/react'

import type {
  EuiBasicTableColumn,
  EuiComboBoxOptionOption,
  EuiTabbedContentTab,
} from '@elastic/eui'
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiSpacer,
  EuiTabbedContent,
  EuiText,
  EuiToolTip,
} from '@elastic/eui'

import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { FormattedMessage } from '@modules/i18n'
import type {
  ProjectRoleAssignment,
  DeploymentRoleAssignment,
  RoleAssignments,
} from '@modules/cloud-api/v1/types'
import { CuiAlert } from '@modules/cui/Alert'
import {
  filterItemsByIdOrName,
  getAllRoleAssignments,
  getDefaultEsKibanaApiAccessValue,
  getEsKibanaApiAccess,
  normaliseDeploymentRoleAssignments,
  normaliseProjectRoleAssignments,
  replaceAllRoleAssignment,
  roleAssignmentsToTableItems,
  setDeploymentRoleAssignments,
  setProjectRoleAssignments,
  tableItemsToDeploymentRoleAssignment,
  tableItemsToProjectRoleAssignment,
  updateTableItemRoles,
} from '@modules/role-assignments-lib'
import {
  type ResourceType,
  type RoleAssignmentsTableItem,
  type ResourceRoleAssignment,
  type NormalisedResourceRoleAssignment,
  type ResourceRoleId,
  type Resource,
  type RoleOptionDefinition,
  type TableItemRoleDescriptor,
  isProjectResourceType,
  isCloudSpecificProjectRoleId,
} from '@modules/role-assignments-lib/types'
import {
  ccmClusterOptions,
  deploymentOptions,
  elasticsearchOptions,
  observabilityOptions,
  securityOptions,
  securityOptionsWithSearchAiLake,
} from '@modules/role-assignments-lib/constants'
import { ApiAccessSelectorTitle, ResourceTypeDisplay } from '@modules/role-assignments-lib/messages'
import { useConfig } from '@modules/cui/ConfigContext'
import { AdminRoleSelector } from '@modules/role-assignments-admin-components/ProjectRoleAssignmentsEditor/AdminRoleSelector'
import { UserRoleSelector } from '@modules/role-assignments-user-components/ProjectRoleAssignmentsEditor/UserRoleSelector'

import ProjectRoleAssignmentsTable from '../ProjectRoleAssignmentsTable'
import ApiAccessSelector from '../ApiAccessSelector'

import AllRoleSelector from './AllRoleSelector'
import AllRoleSelectorUiam from './AllRoleSelectorUiam'
import AllRoleSelectorAccess from './AllRoleSelectorAccess'
import { getSelectedOptions } from './lib'

import type { ApiAccessOptions } from '../ApiAccessSelector/types'
import type { CSSObject } from '@emotion/react'
import type { FunctionComponent, ReactElement } from 'react'

export type QueryResult<T extends ResourceType> = {
  data: Array<Resource<T>> | undefined
  isEnabled: boolean
  isLoading: boolean
  error: unknown
}

export type ProjectRoleAssignmentsEditorProps = {
  organizationId: string
  roleAssignments: RoleAssignments
  deploymentQuery: QueryResult<'deployment'>
  elasticsearchQuery: QueryResult<'elasticsearch'>
  observabilityQuery: QueryResult<'observability'>
  securityQuery: QueryResult<'security'>
  onChangeRoleAssignments: (roleAssignments: RoleAssignments) => void
  customRolesEnabled: boolean
  observabilityCustomRoles: boolean
  isApiKey?: boolean
}

const formattedTabName = (name: ResourceType, isUIAMEnabled: boolean): ReactElement => {
  switch (name) {
    case 'deployment':
      return (
        <FormattedMessage
          id='deployments.pretty.type.resource'
          defaultMessage={isUIAMEnabled ? 'Hosted deployments' : 'Deployment'}
        />
      )
    case 'elasticsearch':
      return (
        <FormattedMessage
          id='projects.pretty.type.elasticsearch.resource'
          defaultMessage={isUIAMEnabled ? 'Elasticsearch projects' : 'Elasticsearch'}
        />
      )
    case 'observability':
      return (
        <FormattedMessage
          id='projects.pretty.type.observability.resource'
          defaultMessage={isUIAMEnabled ? 'Observability projects' : 'Observability'}
        />
      )
    case 'security':
      return (
        <FormattedMessage
          id='projects.pretty.type.security.resource'
          defaultMessage={isUIAMEnabled ? 'Security projects' : 'Security'}
        />
      )
    default:
      return <FormattedMessage id='projects.pretty.type.default.resource' defaultMessage={name} />
  }
}

const ProjectRoleAssignmentsEditor: FunctionComponent<ProjectRoleAssignmentsEditorProps> = ({
  organizationId,
  roleAssignments,
  deploymentQuery,
  elasticsearchQuery,
  observabilityQuery,
  securityQuery,
  onChangeRoleAssignments,
  customRolesEnabled,
  observabilityCustomRoles,
  isApiKey,
}: ProjectRoleAssignmentsEditorProps) => {
  const [_, flags] = useFlagsWhenLoaded()
  const isUIAMEnabled = flags.uiamUi

  const tabs: EuiTabbedContentTab[] = [
    {
      id: 'deployment',
      prepend: <EuiIcon type='logoElasticStack' />,
      name: formattedTabName('deployment', isUIAMEnabled),
      content: deploymentQuery.isEnabled ? (
        <ProjectTabContent<'deployment', DeploymentRoleAssignment>
          key='deployment'
          organizationId={organizationId}
          resourceType='deployment'
          selectedAllRoleAssignments={getAllRoleAssignments(
            normaliseDeploymentRoleAssignments(roleAssignments.deployment),
          )}
          items={roleAssignmentsToTableItems(
            'deployment',
            deploymentQuery.data,
            roleAssignments.deployment,
            normaliseDeploymentRoleAssignments,
          )}
          isLoading={deploymentQuery.isLoading}
          error={deploymentQuery.error}
          tableItemsToRoleAssignment={tableItemsToDeploymentRoleAssignment(organizationId)}
          onChangeRoleAssignments={(deploymentRoleAssignments) =>
            setDeploymentRoleAssignments(
              roleAssignments,
              deploymentRoleAssignments,
              onChangeRoleAssignments,
            )
          }
          customRolesEnabled={customRolesEnabled}
          observabilityCustomRoles={observabilityCustomRoles}
        />
      ) : null,
    },
    {
      id: 'elasticsearch',
      prepend: <EuiIcon type='logoElasticsearch' />,
      name: formattedTabName('elasticsearch', isUIAMEnabled),
      content: elasticsearchQuery.isEnabled ? (
        <ProjectTabContent<'elasticsearch', ProjectRoleAssignment>
          key='elasticsearch'
          organizationId={organizationId}
          resourceType='elasticsearch'
          selectedAllRoleAssignments={getAllRoleAssignments(
            normaliseProjectRoleAssignments('elasticsearch')(
              roleAssignments.project?.elasticsearch,
            ),
          )}
          items={roleAssignmentsToTableItems(
            'elasticsearch',
            elasticsearchQuery.data,
            roleAssignments.project?.elasticsearch,
            normaliseProjectRoleAssignments('elasticsearch'),
            isApiKey,
          )}
          isLoading={elasticsearchQuery.isLoading}
          error={elasticsearchQuery.error}
          tableItemsToRoleAssignment={tableItemsToProjectRoleAssignment(
            organizationId,
            isUIAMEnabled,
            isApiKey,
          )}
          onChangeRoleAssignments={(elasticsearchRoleAssignments) =>
            setProjectRoleAssignments<'elasticsearch', ProjectRoleAssignment>(
              roleAssignments,
              'elasticsearch',
              elasticsearchRoleAssignments,
              onChangeRoleAssignments,
            )
          }
          customRolesEnabled={customRolesEnabled}
          observabilityCustomRoles={observabilityCustomRoles}
          isApiKey={isApiKey}
        />
      ) : null,
    },
    {
      id: 'observability',
      prepend: <EuiIcon type='logoObservability' />,
      name: formattedTabName('observability', isUIAMEnabled),
      content: observabilityQuery.isEnabled ? (
        <ProjectTabContent<'observability', ProjectRoleAssignment>
          key='observability'
          organizationId={organizationId}
          resourceType='observability'
          selectedAllRoleAssignments={getAllRoleAssignments(
            normaliseProjectRoleAssignments('observability')(
              roleAssignments.project?.observability,
            ),
          )}
          items={roleAssignmentsToTableItems(
            'observability',
            observabilityQuery.data,
            roleAssignments.project?.observability,
            normaliseProjectRoleAssignments('observability'),
            isApiKey,
          )}
          isLoading={observabilityQuery.isLoading}
          error={observabilityQuery.error}
          tableItemsToRoleAssignment={tableItemsToProjectRoleAssignment(
            organizationId,
            isUIAMEnabled,
            isApiKey,
          )}
          onChangeRoleAssignments={(observabilityRoleAssignments) =>
            setProjectRoleAssignments<'observability', ProjectRoleAssignment>(
              roleAssignments,
              'observability',
              observabilityRoleAssignments,
              onChangeRoleAssignments,
            )
          }
          customRolesEnabled={customRolesEnabled}
          observabilityCustomRoles={observabilityCustomRoles}
          isApiKey={isApiKey}
        />
      ) : null,
    },
    {
      id: 'security',
      prepend: <EuiIcon type='logoSecurity' />,
      name: formattedTabName('security', isUIAMEnabled),
      content: securityQuery.isEnabled ? (
        <ProjectTabContent<'security', ProjectRoleAssignment>
          key='security'
          organizationId={organizationId}
          resourceType='security'
          selectedAllRoleAssignments={getAllRoleAssignments(
            normaliseProjectRoleAssignments('security')(roleAssignments.project?.security),
          )}
          items={roleAssignmentsToTableItems(
            'security',
            securityQuery.data,
            roleAssignments.project?.security,
            normaliseProjectRoleAssignments('security'),
            isApiKey,
          )}
          isLoading={securityQuery.isLoading}
          error={securityQuery.error}
          tableItemsToRoleAssignment={tableItemsToProjectRoleAssignment(
            organizationId,
            isUIAMEnabled,
            isApiKey,
          )}
          onChangeRoleAssignments={(securityRoleAssignments) =>
            setProjectRoleAssignments<'security', ProjectRoleAssignment>(
              roleAssignments,
              'security',
              securityRoleAssignments,
              onChangeRoleAssignments,
            )
          }
          customRolesEnabled={customRolesEnabled}
          observabilityCustomRoles={observabilityCustomRoles}
          isApiKey={isApiKey}
        />
      ) : null,
    },
  ]

  const filteredTabs = tabs.filter((tab) => tab.content !== null)

  if (filteredTabs.length === 0) {
    return null
  }

  if (filteredTabs.length === 1) {
    return filteredTabs.at(0)?.content as ReactElement
  }

  return (
    <Fragment>
      {!isUIAMEnabled && (
        <TabsDividers
          showHostedDivider={deploymentQuery.isEnabled}
          showServerlessDivider={
            elasticsearchQuery.isEnabled || observabilityQuery.isEnabled || securityQuery.isEnabled
          }
        />
      )}

      <EuiTabbedContent
        size='m'
        expand={true}
        tabs={filteredTabs}
        initialSelectedTab={filteredTabs[0]}
      />
    </Fragment>
  )
}

type Props<T extends ResourceType, R extends ResourceRoleAssignment<T>> = {
  organizationId: string
  resourceType: T
  selectedAllRoleAssignments: Array<NormalisedResourceRoleAssignment<T>> | undefined
  items: Array<RoleAssignmentsTableItem<T>>
  isLoading: boolean
  error: unknown
  tableItemsToRoleAssignment: (items: Array<RoleAssignmentsTableItem<T>>) => R[]
  onChangeRoleAssignments: (roleAssignments: R[]) => void
  customRolesEnabled: boolean
  observabilityCustomRoles: boolean
  isApiKey?: boolean
}

const ProjectTabContent = <T extends ResourceType, R extends ResourceRoleAssignment<T>>({
  organizationId,
  resourceType,
  selectedAllRoleAssignments,
  items,
  isLoading,
  error,
  tableItemsToRoleAssignment,
  onChangeRoleAssignments,
  customRolesEnabled,
  observabilityCustomRoles,
  isApiKey,
}: Props<T, R>) => {
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'
  const [_, flags] = useFlagsWhenLoaded()
  const isUIAMEnabled = flags.uiamUi

  const [filterQuery, setFilterQuery] = useState('')
  const [allRole, setAllRole] = useState<boolean>(!!selectedAllRoleAssignments?.length)
  const filteredItems = filterItemsByIdOrName(items, filterQuery)
  const { getOptions, noRolesPlaceholder } = useGetOptions()

  const options = getOptions(resourceType)

  const singleSelection = resourceType === 'deployment' || !customRolesEnabled
  const singleSelectionAsCount = singleSelection ? 1 : 2

  const [apiAccessValue, setApiAccessValue] = useState(
    getEsKibanaApiAccess(selectedAllRoleAssignments ?? [], resourceType, isApiKey),
  )

  const updateApiAccessValue = (value: ApiAccessOptions) => {
    const selectedOptions = getSelectedOptions(
      selectedAllRoleAssignments,
      options,
      customRolesEnabled,
    )
    replaceAllRoleAssignment(
      resourceType,
      organizationId,
      selectedOptions,
      onChangeRoleAssignments,
      value,
      isApiKey,
    )

    setApiAccessValue(value)
  }

  return (
    <Fragment>
      <EuiSpacer />

      <EuiFlexGroup direction='column' gutterSize='l'>
        {error && (
          <EuiFlexItem>
            <CuiAlert type='danger'>{error}</CuiAlert>
          </EuiFlexItem>
        )}

        {isUIAMEnabled ? (
          <AllRoleSelectorUiam
            allRole={allRole}
            setAllRole={setAllRole}
            replaceAllRoleAssignment={() => {
              replaceAllRoleAssignment(
                resourceType,
                organizationId,
                [],
                onChangeRoleAssignments,
                apiAccessValue,
                isApiKey,
              )
            }}
          />
        ) : (
          <AllRoleSelector
            data-test-id={`allRoleSelector-${resourceType}`}
            organizationId={organizationId}
            options={options}
            customRolesEnabled={customRolesEnabled}
            singleSelection={singleSelection}
            singleSelectionAsCount={singleSelectionAsCount}
            noRolesPlaceholder={noRolesPlaceholder}
            selectedOptions={getSelectedOptions(
              selectedAllRoleAssignments,
              options,
              customRolesEnabled,
            )}
            resourceTypeDisplay={<ResourceTypeDisplay resourceType={resourceType} plural={true} />}
            onChange={(selectedOptions) => {
              replaceAllRoleAssignment(
                resourceType,
                organizationId,
                selectedOptions,
                onChangeRoleAssignments,
              )
            }}
          />
        )}
        <EuiFlexItem>
          {allRole && isUIAMEnabled ? (
            <AllRoleSelectorAccess
              options={options}
              singleSelection={singleSelection}
              singleSelectionAsCount={singleSelectionAsCount}
              noRolesPlaceholder={noRolesPlaceholder}
              apiAccessValue={apiAccessValue ?? getDefaultEsKibanaApiAccessValue(isApiKey)}
              setApiAccessValue={updateApiAccessValue}
              data-test-id={`allRoleSelector-${resourceType}`}
              onChange={(selectedOptions) => {
                replaceAllRoleAssignment(
                  resourceType,
                  organizationId,
                  selectedOptions,
                  onChangeRoleAssignments,
                  apiAccessValue,
                  isApiKey,
                )
              }}
              selectedOptions={getSelectedOptions(
                selectedAllRoleAssignments,
                options,
                customRolesEnabled,
              )}
              isApiKey={isApiKey}
              resourceType={resourceType}
            />
          ) : (
            <ProjectRoleAssignmentsTable<T>
              filteredItems={filteredItems}
              isLoading={isLoading}
              onSearch={setFilterQuery}
              columns={getColumns(
                resourceType,
                items,
                tableItemsToRoleAssignment,
                onChangeRoleAssignments,
                customRolesEnabled,
                observabilityCustomRoles,
                isAdminConsole,
                isUIAMEnabled,
                isApiKey,
              )}
            />
          )}
        </EuiFlexItem>
      </EuiFlexGroup>
    </Fragment>
  )
}

const useGetOptions = () => {
  const { formatMessage } = useIntl()

  const securityPredefinedGroup = {
    label: formatMessage({
      id: 'security.essentials-complete.roles-assignments.predefined-roles-group.label',
      defaultMessage: 'Essentials/Complete predefined roles',
    }),
    isGroupLabelOption: true,
    'data-test-subj': 'predefinedRolesGroup',
  }

  const securityAISocEnginePredefinedGroup = {
    label: formatMessage({
      id: 'security.ai-soc-engine.roles-assignments.predefined-roles-group.label',
      defaultMessage: 'AI SOC Engine predefined roles',
    }),
    isGroupLabelOption: true,
    'data-test-subj': 'predefinedRolesGroup',
  }

  const [isFlagReady, flags] = useFlagsWhenLoaded()
  const showAiSocEngineRoles = isFlagReady && flags.aiSocRoles

  const getOptions = <T extends ResourceType>(
    resourceType: T,
  ): Array<EuiComboBoxOptionOption<ResourceRoleId<T>>> => {
    const getMappedRoles = (predefinedRoles: RoleOptionDefinition[]) =>
      predefinedRoles.map(
        (roleDef) =>
          ({
            value: roleDef.roleId,
            label: formatMessage({ ...roleDef.labelDescriptor }),
            toolTipContent: formatMessage(
              { ...roleDef.descriptionDescriptor },
              { strong: (content) => <strong>{content}</strong>, br: <br /> },
            ),
            title: '',
          }) as EuiComboBoxOptionOption<ResourceRoleId<T>>,
      )

    const predefinedRoles = {
      deployment: deploymentOptions,
      elasticsearch: elasticsearchOptions,
      observability: observabilityOptions,
      security: securityOptions,
      'cloud-connected': ccmClusterOptions,
    }[resourceType]

    if (resourceType === 'security') {
      return [
        ...(showAiSocEngineRoles ? [securityPredefinedGroup] : []),
        ...getMappedRoles(predefinedRoles),
        ...(showAiSocEngineRoles
          ? [
              ...[securityAISocEnginePredefinedGroup],
              ...getMappedRoles(securityOptionsWithSearchAiLake),
            ]
          : []),
      ]
    }

    return getMappedRoles(predefinedRoles)
  }

  const noRolesPlaceholder = (singleSelection: boolean) =>
    formatMessage(
      {
        id: 'roles-assignments.all.none.placeholder',
        defaultMessage: 'No {count, plural, one {role} other {roles}} assigned',
      },
      {
        count: singleSelection ? 1 : 2,
      },
    )

  return { getOptions, noRolesPlaceholder }
}

const line: CSSObject = {
  inlineSize: '100%',
  blockSize: '6px',
  borderRadius: '6px',
}

const blueLine = css({
  ...line,
  backgroundColor: '#A2C5E4',
})

const greenLine = css({
  ...line,
  backgroundColor: '#AAE3E0',
})

const TabsDividers: FunctionComponent<{
  showHostedDivider: boolean
  showServerlessDivider: boolean
}> = ({ showHostedDivider, showServerlessDivider }) => (
  <EuiFlexGroup responsive={false} gutterSize='xs'>
    {showHostedDivider && (
      <EuiFlexItem css={{ width: '136px' }} grow={1}>
        <EuiFlexGroup direction='column' gutterSize='s' alignItems='center'>
          <EuiText size='xs'>
            <FormattedMessage id='roles-assignments.hosted' defaultMessage='Hosted' />
          </EuiText>

          <div css={blueLine} />
        </EuiFlexGroup>
      </EuiFlexItem>
    )}

    {showServerlessDivider && (
      <EuiFlexItem grow={3}>
        <EuiFlexGroup direction='column' gutterSize='s' alignItems='center'>
          <EuiText size='xs'>
            <FormattedMessage id='roles-assignments.serverless' defaultMessage='Serverless' />
          </EuiText>

          <div css={greenLine} />
        </EuiFlexGroup>
      </EuiFlexItem>
    )}
  </EuiFlexGroup>
)

const getColumns = <T extends ResourceType, R extends ResourceRoleAssignment<T>>(
  resourceType: T,
  items: Array<RoleAssignmentsTableItem<T>>,
  tableItemsToRoleAssignment: (items: Array<RoleAssignmentsTableItem<T>>) => R[],
  onChangeRoleAssignments: (roleAssignments: Array<ResourceRoleAssignment<T>>) => void,
  customRolesEnabled: boolean,
  observabilityCustomRoles: boolean,
  isAdminConsole: boolean,
  isUIAMEnabled: boolean,
  isApiKey?: boolean,
): Array<EuiBasicTableColumn<RoleAssignmentsTableItem<T>>> => {
  const singleSelection = resourceType === 'deployment' || !customRolesEnabled
  const columns: Array<EuiBasicTableColumn<RoleAssignmentsTableItem<T>>> = [
    {
      name: <FormattedMessage id='roles-assignments.name' defaultMessage='Name' />,
      field: 'name',
      sortable: ({ name }) => name.toLowerCase(),
      render: (name: string) => (
        <EuiToolTip content={name} position='left' display='block'>
          <span style={{ display: 'block' }} className='eui-textTruncate'>
            {name}
          </span>
        </EuiToolTip>
      ),
      truncateText: true,
      width: isUIAMEnabled ? '30%' : '50%',
    },
    {
      name: (
        <FormattedMessage
          id='roles-assignments.role'
          defaultMessage='{count, plural, one {Role} other {Roles}}'
          values={{
            count: singleSelection ? 1 : 2,
          }}
        />
      ),
      field: 'roles',
      render: (roles: TableItemRoleDescriptor[], { id, roleManagementUrl, isDisabled }) =>
        isAdminConsole ? (
          <AdminRoleSelector
            resourceType={resourceType}
            items={items}
            tableItemsToRoleAssignment={tableItemsToRoleAssignment}
            onChangeRoleAssignments={onChangeRoleAssignments}
            roles={roles}
            id={id}
            roleManagementUrl={roleManagementUrl}
            isDisabled={isDisabled}
          />
        ) : (
          <UserRoleSelector
            resourceType={resourceType}
            items={items}
            tableItemsToRoleAssignment={tableItemsToRoleAssignment}
            onChangeRoleAssignments={onChangeRoleAssignments}
            roles={roles}
            id={id}
            roleManagementUrl={roleManagementUrl}
            isDisabled={isDisabled}
            customRolesEnabled={customRolesEnabled}
            observabilityCustomRoles={observabilityCustomRoles}
          />
        ),
      width: isUIAMEnabled ? '30%' : '50%',
    },
  ]

  if (isUIAMEnabled && isProjectResourceType(resourceType)) {
    columns.push({
      name: <ApiAccessSelectorTitle isApiKey={isApiKey ?? false} isOrgOwner={false} />,
      field: 'roles',
      render: (roles: TableItemRoleDescriptor[], item: RoleAssignmentsTableItem<T>) => (
        <div style={{ width: '100%' }}>
          <ApiAccessSelector
            containsCustomRoles={roles.some((role) => role.isCustomRole)}
            containsNonCloudSpecificProjectRoles={roles.some(
              (role) => !isCloudSpecificProjectRoleId(role.roleId),
            )}
            onChange={(value) => {
              updateTableItemRoles(
                items,
                item.id,
                roles,
                tableItemsToRoleAssignment,
                onChangeRoleAssignments,
                value,
              )
            }}
            includeLabel={false}
            projectId={item.id}
            defaultValue={item.apiAccess}
            isDisabled={roles.length === 0 || item.isDisabled}
            isApiKey={isApiKey}
            resourceType={resourceType}
          />
        </div>
      ),
      width: '40%',
    })
  }

  return columns
}

export default ProjectRoleAssignmentsEditor
